import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Plus, Minus } from "@phosphor-icons/react";
import { MaskReveal, FadeUp } from "@/components/Motion";
import { Eyebrow, CTASection } from "@/components/Shared";
import { FAQS } from "@/data/content";

function Item({ item, index, open, onToggle }) {
  return (
    <FadeUp delay={index * 0.03}>
      <div
        className={`rounded-[1.6rem] border transition-colors duration-300 ${
          open ? "border-ol-blue/40 bg-white soft-shadow-sm" : "border-gray-100 bg-white"
        }`}
      >
        <button
          onClick={onToggle}
          data-testid={`faq-toggle-${index}`}
          className="flex w-full items-center justify-between gap-4 p-6 sm:p-7 text-left"
          aria-expanded={open}
        >
          <span className="text-lg sm:text-xl font-bold text-ol-ink">{item.q}</span>
          <span className={`grid place-items-center h-10 w-10 shrink-0 rounded-full transition-colors duration-200 ${open ? "bg-ol-blue text-white" : "bg-ol-surface text-ol-blue"}`}>
            {open ? <Minus size={20} weight="bold" /> : <Plus size={20} weight="bold" />}
          </span>
        </button>
        <AnimatePresence initial={false}>
          {open && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: "auto", opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.32, ease: [0.16, 1, 0.3, 1] }}
              className="overflow-hidden"
            >
              <p className="px-6 sm:px-7 pb-7 -mt-1 text-ol-gray leading-relaxed text-[17px]" data-testid={`faq-answer-${index}`}>
                {item.a}
              </p>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </FadeUp>
  );
}

export default function FAQ() {
  const [open, setOpen] = useState(0);
  return (
    <div data-testid="faq-page">
      <section className="relative overflow-hidden bg-white pt-28 sm:pt-36 pb-10">
        <div className="absolute inset-0 dot-grid opacity-50" />
        <div className="relative z-10 mx-auto max-w-3xl px-5 sm:px-8 text-center">
          <FadeUp className="flex justify-center"><Eyebrow>Answers</Eyebrow></FadeUp>
          <h1 className="mt-6 text-[2.4rem] sm:text-6xl font-extrabold tracking-tight text-ol-ink leading-[1.02]">
            <MaskReveal lines={["Frequently asked"]} delay={0.1} />
            <MaskReveal lines={["questions."]} delay={0.24} lineClass="text-ol-blue" />
          </h1>
          <p className="mt-5 text-lg text-ol-gray leading-relaxed">
            Everything you need to know about the one application, one verification, one profile system.
          </p>
        </div>
      </section>

      <section className="px-5 sm:px-8 pb-8" data-testid="faq-list">
        <div className="mx-auto max-w-3xl space-y-4">
          {FAQS.map((item, i) => (
            <Item key={i} item={item} index={i} open={open === i} onToggle={() => setOpen(open === i ? -1 : i)} />
          ))}
        </div>
      </section>

      <CTASection testId="faq-cta" title="Still have questions?" subtitle="Join the waitlist and we'll keep you posted with everything you need before launch." />
    </div>
  );
}
